import {
  FC,
  SyntheticEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Button,
  H1,
  InfoBlock,
  OpenedEyeIcon,
  PosetiteliIcon,
  Separator,
} from '@smartshell/ui-kit';
import cn from 'classnames';

import { AuthContext } from 'auth';
import { useAdvertisementMutations, useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import { AdvertisementUnpublishedModal } from 'modals';
import { openConfirmDialog } from 'services/Confirmation';
import { EAdvertisementStatus, IMyAdvertisementMetrics } from 'types';
import { formatExpirationDate, formatSum } from 'utils';

import { IMyAdvertisementItemProps } from './interfaces';
import s from './MyAdvertisementItem.module.scss';
import {
  getActionButton,
  getAdvertisementLabel,
  getDropdownTextAction,
} from './utils';

export const MyAdvertisementItem: FC<IMyAdvertisementItemProps> = ({
  data,
  setAdvertisementManagerState,
  setAdvertisementViewState,
}) => {
  const { t, lang } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [openUnpublishedModal, setOpenUnpublishedModal] =
    useState<boolean>(false);

  const IMAGE_PROXY_BASE = process.env.REACT_APP_IMAGE_PROXY_BASE;

  const toggleDropdown = (
    e: SyntheticEvent<HTMLButtonElement, Event>
  ): void => {
    e.stopPropagation();
    setIsDropdownOpen(!isDropdownOpen);
  };

  const closeDropdown = (): void => {
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (!dropdownRef?.current?.contains(event.target as Node)) {
        closeDropdown();
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const {
    handledDeleteAdvertisement,
    deleting,
    handledChangeAdvertisementStatus,
    changingStatus,
    handledRepublishAdvertisement,
    republishing,
  } = useAdvertisementMutations(t);

  const disabled = !permissions?.has('advertisement_manage');

  const size = useWindowResize();
  return (
    <>
      {openUnpublishedModal && (
        <AdvertisementUnpublishedModal
          onClose={() => setOpenUnpublishedModal(false)}
          handledChangeAdvertisementStatus={handledChangeAdvertisementStatus}
          changingStatus={changingStatus}
          data={data}
        />
      )}
      {isDropdownOpen && <div className={s.mobile_frame}></div>}
      <div
        className={s.container}
        onClick={() =>
          setAdvertisementViewState({ mode: 'my', advertisement: data })
        }
      >
        <div className={s.left}>
          <div
            className={s.blur}
            style={{
              backgroundImage: `url(${IMAGE_PROXY_BASE}/rs:fit:170:170/g:sm/plain/${data?.images[0]})`,
            }}
          >
            <img
              className={s.image}
              src={`${IMAGE_PROXY_BASE}/rs:fit:170:170/g:sm/plain/${data?.images[0]}`}
              srcSet={`
                ${IMAGE_PROXY_BASE}/rs:fit:170:170/g:sm/plain/${data?.images[0]} 1x,
                ${IMAGE_PROXY_BASE}/rs:fit:340:340/g:sm/plain/${data?.images[0]} 2x,
                ${IMAGE_PROXY_BASE}/rs:fit:510:510/g:sm/plain/${data?.images[0]} 3x,
      `}
              alt="preview"
            />
          </div>
        </div>
        <div className={s.right}>
          <div className={s.header}>
            <div className={s.header_left}>
              <span className={s.title}>{data?.title}</span>
              {data && (
                <H1 className={s.cost}>
                  {formatSum(data?.price, data?.currency?.letter)}
                </H1>
              )}
            </div>
            <div className={s.header_right}>
              {getAdvertisementLabel(t, data?.status, size.width < 960)}
              {data?.status === EAdvertisementStatus.ACTIVE && (
                <span className={s.date}>
                  {formatExpirationDate(data?.expires_at, lang, t('until_2'))}
                </span>
              )}
            </div>
          </div>

          {size.width > 960 &&
            data?.status === EAdvertisementStatus.REJECTED &&
            data?.moderation?.comment && (
              <InfoBlock
                className={s.info_block}
                color="red"
                iconName="CircleCloseIcon"
                title={t('advertisement.info.rejected')}
                message={data?.moderation?.comment}
              />
            )}

          {size.width > 960 &&
            data?.status === EAdvertisementStatus.EXPIRED && (
              <InfoBlock
                className={cn(s.info_block, s.gray)}
                iconName="ClockIcon"
                title={t('advertisement.info.expired.title')}
                message={t('advertisement.info.expired.description')}
              />
            )}
          {data?.status !== EAdvertisementStatus.MODERATION &&
            data?.status !== EAdvertisementStatus.REJECTED && (
              <div className={s.info}>
                <div className={s.info_block}>
                  <OpenedEyeIcon size="s" color="secondary" />
                  <span className={s.count}>{data?.metrics.views}</span>
                </div>
                <div className={s.info_block}>
                  <PosetiteliIcon size="s" color="secondary" />
                  <span className={s.count}>
                    {(data?.metrics as IMyAdvertisementMetrics).request_contact}
                  </span>
                </div>
              </div>
            )}

          <div className={s.footer}>
            <Separator className={s.separator} />
            <div className={s.buttons_block}>
              {getActionButton(
                t,
                setAdvertisementManagerState,
                setOpenUnpublishedModal,
                handledRepublishAdvertisement,
                data,
                deleting || changingStatus || republishing,
                disabled
              )}
              <div className={s.actions}>
                <Button
                  className={s.button}
                  variant="secondary"
                  onlyIcon
                  disabled={!permissions?.has('advertisement_manage')}
                  iconName="DotsMenuIcon"
                  onClick={(e) => toggleDropdown(e)}
                />
                {isDropdownOpen && (
                  <ul className={cn(s.dropdown)}>
                    {data?.status !== EAdvertisementStatus.REJECTED &&
                      data?.status !== EAdvertisementStatus.MODERATION && (
                        <li
                          className={s.item}
                          onClick={(e) => {
                            e.stopPropagation();

                            if (data?.status === 'ACTIVE') {
                              setOpenUnpublishedModal(true);
                            } else if (
                              data?.status === EAdvertisementStatus.COMPLETED ||
                              data?.status === EAdvertisementStatus.REJECTED ||
                              data?.status === EAdvertisementStatus.EXPIRED
                            ) {
                              handledRepublishAdvertisement(data?.id);
                            }
                            setIsDropdownOpen(false);
                          }}
                        >
                          {getDropdownTextAction(t, data?.status)}
                        </li>
                      )}
                    <li
                      className={s.item}
                      onClick={(e) => {
                        e.stopPropagation();
                        setAdvertisementViewState(null);

                        setAdvertisementManagerState({
                          mode: 'edit',
                          advertisement: data,
                        });
                        setIsDropdownOpen(false);
                      }}
                    >
                      {t('edit')}
                    </li>
                    <Separator className={s.separator} />
                    <li
                      className={cn(s.item, s.red)}
                      onClick={(e) => {
                        e.stopPropagation();

                        if (data) {
                          openConfirmDialog(t, 'remove', {
                            title: t('advertisement.delete.title'),
                            message: t(
                              'advertisement.delete.description',
                              data?.title
                            ),
                            acceptTitle: t('delete'),
                            declineTitle: t('cancel'),
                          }).then(({ status }) => {
                            if (status === 'ACCEPTED') {
                              handledDeleteAdvertisement(data?.id);
                            }
                          });
                        }
                        setIsDropdownOpen(false);
                      }}
                    >
                      {t('delete')}
                    </li>
                    <Separator className={s.separator_mobile} />
                    <li
                      className={cn(s.item, s.item_mobile)}
                      onClick={(e) => {
                        e.stopPropagation();

                        setIsDropdownOpen(false);
                      }}
                    >
                      {t('cancel')}
                    </li>
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
